import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, RefreshCw, Clock, CheckCircle, Truck, ShoppingCart, Star, Loader2, AlertCircle, Download, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusIcons = {
  pending_approval: AlertCircle,
  confirmed: CheckCircle,
  processing: Clock,
  shipped: Truck,
  delivered: Package,
  completed: Package,
  cancelled: AlertCircle,
  rejected: AlertCircle,
};

const statusColors = {
  pending_approval: 'text-yellow-600 bg-yellow-50',
  confirmed: 'text-blue-600 bg-blue-50',
  processing: 'text-orange-600 bg-orange-50',
  shipped: 'text-purple-600 bg-purple-50',
  delivered: 'text-green-600 bg-green-50',
  completed: 'text-green-700 bg-green-50',
  cancelled: 'text-red-600 bg-red-50',
  rejected: 'text-red-600 bg-red-50',
};

const BuyerDashboard = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [frequentItems, setFrequentItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, frequentRes] = await Promise.all([
        axios.get(`${API}/my-orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/my-orders/frequent-items`, { headers: getAuthHeader() })
      ]);
      setOrders(ordersRes.data);
      setFrequentItems(frequentRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId) => {
    try {
      const res = await axios.post(`${API}/my-orders/reorder/${orderId}`, {}, { headers: getAuthHeader() });
      const items = res.data.items.filter(i => i.available);
      items.forEach(item => {
        addToCart({ id: item.product_id, name: item.name, price: item.price, image: item.image }, item.quantity);
      });
      toast({ title: "Items added to cart", description: `${items.length} items from order ${orderId} added to your cart.` });
    } catch (err) {
      toast({ title: "Reorder failed", description: "Some items may no longer be available.", variant: "destructive" });
    }
  };

  const handleAddFrequentItem = (item) => {
    addToCart({ id: item.product_id, name: item.name, price: item.price, image: item.image }, 1);
    toast({ title: "Added to cart", description: `${item.name} added to your cart.` });
  };

  const handleCancelOrder = async (orderId) => {
    const reason = window.prompt('Cancel this order? You can include an optional note for the admin:', '');
    if (reason === null) return; // user dismissed
    try {
      await axios.post(`${API}/my-orders/${orderId}/cancel`, { reason }, { headers: getAuthHeader() });
      toast({ title: 'Order cancelled', description: orderId });
      fetchData();
    } catch (e) {
      toast({ title: 'Cancel failed', description: e.response?.data?.detail || 'Could not cancel order', variant: 'destructive' });
    }
  };

  const downloadOrderPdf = (orderId, kind) => {
    fetch(`${API}/orders/${orderId}/${kind}.pdf`, { headers: getAuthHeader() })
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${kind === 'invoice' ? 'invoice' : 'order'}-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: 'Download failed', variant: 'destructive' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="buyer-dashboard">
      <div className="bg-gradient-to-r from-purple-600 to-slate-700 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-4xl font-bold">Welcome back, {user?.name || 'Buyer'}</h1>
          <p className="text-white/80 mt-1 text-sm sm:text-base">{user?.company && `${user.company} | `}{user?.email}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card className="border-2">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-purple-600">{orders.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Total Orders</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                {orders.filter(o => ['confirmed', 'processing', 'shipped'].includes(o.status)).length}
              </p>
              <p className="text-xs sm:text-sm text-slate-500">Active Orders</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-yellow-600">
                {orders.filter(o => o.status === 'pending_approval').length}
              </p>
              <p className="text-xs sm:text-sm text-slate-500">Pending Approval</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-green-600">{frequentItems.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Frequent Items</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === 'orders' ? 'border-purple-600 text-purple-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="orders-tab"
          >
            <Package className="h-4 w-4 inline mr-1" /> Order History
          </button>
          <button
            onClick={() => setActiveTab('frequent')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === 'frequent' ? 'border-purple-600 text-purple-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="frequent-tab"
          >
            <Star className="h-4 w-4 inline mr-1" /> Frequent Items
          </button>
        </div>

        {/* Order History Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-3" data-testid="orders-list">
            {orders.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-8 text-center">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No orders yet. Start shopping to see your order history here.</p>
                  <Link to="/products"><Button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white">Browse Products</Button></Link>
                </CardContent>
              </Card>
            ) : (
              orders.map((order) => {
                const StatusIcon = statusIcons[order.status] || Package;
                return (
                  <Card key={order.order_id} className="border-2 hover:shadow-md transition" data-testid={`order-${order.order_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-800">{order.order_id}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[order.status] || 'text-slate-600 bg-slate-50'}`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {order.status.replace(/_/g, ' ')}
                            </span>
                            {order.requires_approval && order.status === 'pending_approval' && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Awaiting Approval</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {order.items?.length} item(s) | ${order.total?.toFixed(2)} | {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => handleReorder(order.order_id)} data-testid={`reorder-${order.order_id}`}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Reorder
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => downloadOrderPdf(order.order_id, order.status === 'pending_approval' ? 'confirmation' : 'invoice')} data-testid={`download-${order.order_id}`}>
                            <Download className="h-3 w-3 mr-1" /> {order.status === 'pending_approval' ? 'Confirmation' : 'Invoice'}
                          </Button>
                          {order.status === 'pending_approval' && (
                            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleCancelOrder(order.order_id)} data-testid={`cancel-${order.order_id}`}>
                              <XCircle className="h-3 w-3 mr-1" /> Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Items preview */}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <span key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">
                            {item.name?.substring(0, 25)}{item.name?.length > 25 ? '...' : ''} x{item.quantity}
                          </span>
                        ))}
                        {order.items?.length > 3 && (
                          <span className="text-xs text-slate-400">+{order.items.length - 3} more</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Frequent Items Tab */}
        {activeTab === 'frequent' && (
          <div data-testid="frequent-items-list">
            {frequentItems.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-8 text-center">
                  <Star className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Order some products and your frequently ordered items will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {frequentItems.map((item) => (
                  <Card key={item.product_id} className="border-2 hover:shadow-md transition" data-testid={`frequent-${item.product_id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">{item.name}</p>
                        <p className="text-purple-600 font-semibold text-sm">${item.price?.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">Ordered {item.order_count}x ({item.total_ordered} units)</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddFrequentItem(item)}
                        disabled={item.stock === 0}
                      >
                        <ShoppingCart className="h-3 w-3" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerDashboard;
